// document 级悬停面板：一张稳定的实体卡片同时承载卡面、摘要与完整规则。
import { useEffect, useSyncExternalStore } from "react";
import type { CSSProperties } from "react";
import { CardBack, ReviewedText } from "./assets.tsx";
import type { ReviewedLine } from "./reviewed-copy.ts";


type TipSide = "left" | "right" | "top" | "bottom" | "center";
type TipLayout = "standard" | "card";

interface TipState {
  title: string;
  kicker: string;
  summary: string;
  detail: string;
  detailLabel: string;
  meta: string;
  metaLabel: string;
  image: string;
  color: string;
  layout: TipLayout;
  titleRuns: ReviewedLine | null;
  reviewLines: readonly ReviewedLine[];
  x: number;
  y: number;
  width: number;
  maxHeight: number;
  side: TipSide;
}

let state: TipState | null = null;
let activeHost: HTMLElement | null = null;
let positionFrame: number | null = null;
let pendingPoint: { x: number; y: number } | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function place(clientX: number, clientY: number, layout: TipLayout, hasImage: boolean): Pick<TipState, "x" | "y" | "side" | "width" | "maxHeight"> {
  const margin = 12;
  const gap = 18;
  const viewport = window.visualViewport;
  const viewportWidth = viewport?.width ?? window.innerWidth;
  const viewportHeight = viewport?.height ?? window.innerHeight;
  const left = (viewport?.offsetLeft ?? 0) + margin;
  const top = (viewport?.offsetTop ?? 0) + margin;
  const right = left + viewportWidth - margin * 2;
  const bottom = top + viewportHeight - margin * 2;
  const width = Math.min(
    layout === "card" ? 520 : hasImage ? 430 : 360,
    Math.max(1, right - left),
  );
  const estimatedHeight = Math.min(
    layout === "card" ? 410 : hasImage ? 290 : 230,
    Math.max(1, bottom - top),
  );
  const centered = {
    x: (left + right) / 2, y: top, side: "center" as const,
    width, maxHeight: Math.max(1, bottom - top),
  };
  if (viewportWidth < 760 || viewportHeight < 640) return centered;

  const y = clamp(clientY - Math.min(28, estimatedHeight * 0.12), top, bottom - estimatedHeight);
  if (right - clientX - gap >= width) {
    return { x: clientX + gap, y, side: "right", width, maxHeight: bottom - y };
  }
  if (clientX - gap - left >= width) {
    return { x: clientX - gap, y, side: "left", width, maxHeight: bottom - y };
  }

  const centerX = clamp(clientX, left + width / 2, right - width / 2);
  if (clientY - gap - top >= estimatedHeight) {
    return { x: centerX, y: clientY - gap, side: "top", width, maxHeight: clientY - gap - top };
  }
  if (bottom - clientY - gap >= estimatedHeight) {
    return { x: centerX, y: clientY + gap, side: "bottom", width, maxHeight: bottom - clientY - gap };
  }
  return centered;
}

function parsedLines(value: string | undefined): readonly ReviewedLine[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? (parsed as ReviewedLine[]) : [];
  } catch {
    return [];
  }
}

function parsedLine(value: string | undefined): ReviewedLine | null {
  const lines = parsedLines(value ? `[${value}]` : undefined);
  return lines[0] ?? null;
}

function show(host: HTMLElement, clientX: number, clientY: number): void {
  const title = host.dataset["tipTitle"] ?? "";
  if (!title) {
    hide();
    return;
  }

  activeHost = host;
  const image = host.dataset["tipImage"] ?? "";
  const layout: TipLayout = host.dataset["tipLayout"] === "card" ? "card" : "standard";
  state = {
    title,
    kicker: host.dataset["tipKicker"] ?? "",
    summary: host.dataset["tipSummary"] ?? "",
    detail: host.dataset["tipDetail"] ?? "",
    detailLabel: host.dataset["tipDetailLabel"] ?? "规则详情",
    meta: host.dataset["tipMeta"] ?? "",
    metaLabel: host.dataset["tipMetaLabel"] ?? "补充规则",
    image,
    color: host.dataset["tipColor"] ?? "#c9a227",
    titleRuns: parsedLine(host.dataset["tipTitleRuns"]),
    reviewLines: parsedLines(host.dataset["tipReviewLines"]),
    layout,
    ...place(clientX, clientY, layout, Boolean(image)),
  };
  emit();
}

function hide(): void {
  activeHost = null;
  pendingPoint = null;
  if (positionFrame !== null) {
    window.cancelAnimationFrame(positionFrame);
    positionFrame = null;
  }
  if (!state) return;
  state = null;
  emit();
}

function useTooltip(): TipState | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

export function TooltipLayer() {
  const current = useTooltip();

  useEffect(() => {
    let pointerHost: HTMLElement | null = null;
    let focusedHost: HTMLElement | null = null;
    let dismissedHost: HTMLElement | null = null;
    const releaseDismissal = (): void => {
      if (dismissedHost !== pointerHost && dismissedHost !== focusedHost) dismissedHost = null;
    };
    const showAtHost = (host: HTMLElement | null): void => {
      if (!host || host === dismissedHost) {
        hide();
        return;
      }
      const bounds = host.getBoundingClientRect();
      show(host, bounds.left + bounds.width / 2, bounds.top + bounds.height / 2);
    };
    const dismiss = (): void => {
      dismissedHost = activeHost ?? pointerHost ?? focusedHost;
      hide();
    };
    const down = (event: PointerEvent): void => {
      if (event.pointerType !== "touch") {
        dismiss();
        return;
      }
      pointerHost = null;
      focusedHost = null;
      dismissedHost = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-tip-title]") : null;
      hide();
    };
    const over = (event: PointerEvent): void => {
      if (event.pointerType === "touch") return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      pointerHost = target.closest<HTMLElement>("[data-tip-title]");
      releaseDismissal();
      if (!pointerHost) {
        showAtHost(focusedHost);
      } else if (pointerHost === dismissedHost) {
        hide();
      } else if (pointerHost !== activeHost) {
        show(pointerHost, event.clientX, event.clientY);
      }
    };
    const move = (event: PointerEvent): void => {
      if (event.pointerType === "touch" || !activeHost || !state) return;
      const target = event.target;
      if (!(target instanceof Node) || !activeHost.contains(target)) return;
      pendingPoint = { x: event.clientX, y: event.clientY };
      if (positionFrame !== null) return;
      positionFrame = window.requestAnimationFrame(() => {
        positionFrame = null;
        if (!state || !pendingPoint) return;
        const next = place(pendingPoint.x, pendingPoint.y, state.layout, Boolean(state.image));
        pendingPoint = null;
        if (state.x === next.x && state.y === next.y && state.side === next.side) return;
        state = { ...state, ...next };
        emit();
      });
    };
    const out = (event: PointerEvent): void => {
      if (!pointerHost) return;
      const next = event.relatedTarget;
      if (next instanceof Node && pointerHost.contains(next)) return;
      pointerHost = null;
      releaseDismissal();
      showAtHost(focusedHost);
    };
    const focusIn = (event: FocusEvent): void => {
      const target = event.target;
      focusedHost = target instanceof HTMLElement ? target.closest<HTMLElement>("[data-tip-title]") : null;
      releaseDismissal();
      showAtHost(focusedHost);
    };
    const focusOut = (event: FocusEvent): void => {
      const next = event.relatedTarget;
      if (next instanceof Node && focusedHost?.contains(next)) return;
      focusedHost = null;
      releaseDismissal();
      showAtHost(pointerHost);
    };
    const keyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && activeHost) dismiss();
    };
    const reposition = (): void => {
      if (focusedHost && activeHost === focusedHost) showAtHost(focusedHost);
      else hide();
    };
    document.addEventListener("pointerover", over, true);
    document.addEventListener("pointermove", move, true);
    document.addEventListener("pointerout", out, true);
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("focusin", focusIn, true);
    document.addEventListener("focusout", focusOut, true);
    document.addEventListener("keydown", keyDown, true);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", reposition);
    viewport?.addEventListener("scroll", reposition);
    return () => {
      document.removeEventListener("pointerover", over, true);
      document.removeEventListener("pointermove", move, true);
      document.removeEventListener("pointerout", out, true);
      document.removeEventListener("pointerdown", down, true);
      document.removeEventListener("focusin", focusIn, true);
      document.removeEventListener("focusout", focusOut, true);
      document.removeEventListener("keydown", keyDown, true);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      viewport?.removeEventListener("resize", reposition);
      viewport?.removeEventListener("scroll", reposition);
      hide();
    };
  }, []);

  if (!current) return null;
  const accentStyle = {
    "--tip-accent": current.color,
    "--tip-max-height": `${current.maxHeight}px`,
  } as CSSProperties;
  const showCardBack = current.layout === "card" && !current.image;
  return (
    <div
      className={`tip-stack is-${current.side} layout-${current.layout}`}
      style={{ ...accentStyle, left: current.x, top: current.y, width: current.width }}
      role="tooltip"
    >
      <section
        className={`tip-panel tip-unified${current.image || showCardBack ? " has-art" : ""}`}
      >
        {(current.image || showCardBack) && (
          <div className="tip-art">
            {current.image ? (
              <img src={current.image} alt={`${current.title}卡面`} />
            ) : (
              <div className="tip-card-back" aria-label="尚未公开的角色卡">
                <CardBack />
              </div>
            )}
          </div>
        )}
        <div className="tip-copy">
          <header>
            {current.kicker && <span className="tip-kicker">{current.kicker}</span>}
            <strong>
              {current.titleRuns ? (
                <ReviewedText lines={[current.titleRuns]} className="tip-reviewed-title" />
              ) : current.title}
            </strong>
            {current.summary && <p className="tip-summary">{current.summary}</p>}
            {current.reviewLines.length > 0 && (
              <ReviewedText lines={current.reviewLines} className="tip-reviewed-copy" />
            )}
          </header>
          {current.detail && (
            <section className="tip-rule">
              <span className="tip-level-label">{current.detailLabel}</span>
              <p>{current.detail}</p>
            </section>
          )}
          {current.meta && (
            <section className="tip-rule tip-meta">
              <span className="tip-level-label">{current.metaLabel}</span>
              <p>{current.meta}</p>
            </section>
          )}
        </div>
      </section>
    </div>
  );
}
